// The month to date of the key that pays for the caller in this project
// (their own, else the project's) against the admin's monthly limit
// (`GET /api/projects/:projectId/key-limit`), while that key has one: a quiet
// line with a thin bar above every Focus step, red once the month reached the
// limit, its dollars as the budget wall shows them. Inform only: nothing is
// refused. Any failed read (an older server's 404 or answer included) is no
// line.

import { useEffect, useState } from "react";

import { KeyLimitResponseSchema, type KeyLimitMonth } from "@loom/shared/admin";

import { useT } from "../i18n/index.js";
import { httpGet } from "../lib/http";

import { IconAlert } from "./ui";

export const useKeyLimit = (projectId: number): KeyLimitMonth | null => {
  const [month, setMonth] = useState<KeyLimitMonth | null>(null);

  // Read on entering the project and again whenever the tab comes back.
  useEffect(() => {
    let cancelled = false;
    const read = (): void => {
      httpGet(`/api/projects/${projectId}/key-limit`, KeyLimitResponseSchema)
        .then((body) => {
          if (!cancelled) setMonth(body.month);
        })
        .catch(() => {
          if (!cancelled) setMonth(null);
        });
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible") read();
    };
    setMonth(null);
    read();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [projectId]);

  return month;
};

export const KeyLimitBanner = ({ month }: { readonly month: KeyLimitMonth }) => {
  const t = useT();
  const used = Number(month.monthUsd);
  const limit = Number(month.monthlyLimitUsd);
  const shown = { used: used.toFixed(2), limit: limit.toFixed(2) };
  // Reached to the cent the line shows: never "$250.00 of $250.00" in grey.
  const reached = Number(shown.used) >= Number(shown.limit);
  const line = t(
    reached ? `focus.keyLimit.${month.owner}` : `focus.keyLimit.month.${month.owner}`,
    shown,
  );
  const percent = reached ? 100 : Math.floor((used / limit) * 100);
  return (
    <div
      className={reached ? "focus-error focus-key-limit" : "focus-key-limit is-quiet"}
      role={reached ? "status" : undefined}
    >
      {reached && <IconAlert />}
      {line}
      <div
        className="focus-key-limit-bar"
        role="progressbar"
        aria-label={line}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div className="focus-key-limit-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};
